import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Camera, MagnifyingGlass, Plus, UserCircle, CaretDown } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import AddressAutocomplete from '../AddressAutocomplete';
import DropZone from '../DropZone';
import { ContactFormModal, EMPTY_FORM as CONTACT_EMPTY } from '../../pages/ContactsPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Client picker: search existing clients OR create a new one inline.
// Calls onChange({ id, name, address }) whenever the selected client changes.
const ClientPicker = ({ valueId, valueName, onChange, testId = 'project-client-picker' }) => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState({ ...CONTACT_EMPTY, category: 'client' });
  const containerRef = React.useRef(null);

  const fetchClients = React.useCallback(async () => {
    try {
      // Load ALL contacts (any category) so the user can affiliate any type
      // of contact as the project's main client (entreprise, fournisseur,
      // collaborateur or client). Categories are shown as a badge in the
      // dropdown so the choice stays clear.
      const res = await axios.get(`${API_URL}/api/contacts`, { withCredentials: true });
      setClients(res.data || []);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = clients.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q)
      || (c.company || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.phone || '').toLowerCase().includes(q)
      || (c.job_title || '').toLowerCase().includes(q);
  });

  const select = (c) => {
    onChange({ id: c.id, name: c.name, address: c.address || '' });
    setSearch(c.name);
    setOpen(false);
  };

  const openNew = () => {
    setNewForm({ ...CONTACT_EMPTY, category: 'client', name: search });
    setShowNewModal(true);
    setOpen(false);
  };

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/contacts`, newForm, { withCredentials: true });
      toast.success(`Client "${res.data.name}" cree`);
      setShowNewModal(false);
      await fetchClients();
      onChange({ id: res.data.id, name: res.data.name, address: res.data.address || '' });
      setSearch(res.data.name);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur creation client');
    }
  };

  // Display: when a client is selected, show its name (or fallback to free-text valueName)
  const displayValue = (() => {
    if (search) return search;
    if (valueId) {
      const c = clients.find(x => x.id === valueId);
      if (c) return c.name;
    }
    return valueName || '';
  })();

  return (
    <div ref={containerRef} style={{ position: 'relative' }} data-testid={testId}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          className="form-input"
          placeholder="Rechercher un contact (toutes categories) ou en creer un nouveau..."
          value={displayValue}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); if (!e.target.value) onChange({ id: '', name: '', address: '' }); }}
          onFocus={() => setOpen(true)}
          data-testid={`${testId}-input`}
          style={{ paddingRight: '34px' }}
        />
        <CaretDown size={14} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#5E5F66', pointerEvents: 'none' }} />
      </div>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #E2E2E6', borderRadius: '8px', boxShadow: '0 6px 20px rgba(0,0,0,0.12)', maxHeight: '260px', overflow: 'auto', zIndex: 50 }}>
          {filtered.length === 0 && (
            <div style={{ padding: '10px 14px', fontSize: '0.85rem', color: '#5E5F66' }}>
              Aucun client {search ? `correspondant a "${search}"` : 'dans le repertoire'}.
            </div>
          )}
          {filtered.map(c => (
            <div
              key={c.id}
              onClick={() => select(c)}
              data-testid={`${testId}-option-${c.id}`}
              style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #F4F5F7', display: 'flex', alignItems: 'center', gap: '10px' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F5F7'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <UserCircle size={20} color="#2E7D32" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{c.name}</div>
                {(c.company || c.email) && <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{[c.company, c.email].filter(Boolean).join(' - ')}</div>}
              </div>
              {c.category && (
                <span style={{
                  padding: '2px 8px', borderRadius: 999, fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase',
                  color: 'white',
                  background: c.category === 'client' ? '#1565C0'
                    : c.category === 'fournisseur' ? '#B45309'
                    : c.category === 'collaborateur' ? '#2E7D32'
                    : '#6D28D9',
                }}>{c.category}</span>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={openNew}
            data-testid={`${testId}-new-btn`}
            style={{ width: '100%', padding: '10px 14px', backgroundColor: '#E3F2FD', color: '#002FA7', border: 'none', borderTop: '1px solid #E2E2E6', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
          >
            <Plus size={16} /> Creer un nouveau client {search ? `"${search}"` : ''}
          </button>
        </div>
      )}
      <ContactFormModal
        show={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSubmit={submitNew}
        form={newForm}
        setForm={setNewForm}
        editMode={false}
        lockedCategory="client"
      />
    </div>
  );
};

// Taux TVA preset selector with "libre" option (numeric input appears)
const TVA_PRESETS = ['0', '5.5', '10', '20'];
const TvaSelector = ({ value, onChange, testId }) => {
  const numericValue = (value === '' || value === null || value === undefined) ? 20 : parseFloat(value);
  const isPreset = TVA_PRESETS.includes(String(numericValue));
  const [mode, setMode] = React.useState(isPreset ? String(numericValue) : 'libre');
  React.useEffect(() => {
    // Re-sync mode if the value changes externally (e.g. editing an existing project)
    const v = String(numericValue);
    if (TVA_PRESETS.includes(v)) setMode(v); else setMode('libre');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const handleModeChange = (m) => {
    setMode(m);
    if (m !== 'libre') onChange(parseFloat(m));
  };
  return (
    <div style={{ display: 'flex', gap: '6px' }}>
      <Select value={mode} onValueChange={handleModeChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="0">0%</SelectItem>
          <SelectItem value="5.5">5,5%</SelectItem>
          <SelectItem value="10">10%</SelectItem>
          <SelectItem value="20">20%</SelectItem>
          <SelectItem value="libre">Libre</SelectItem>
        </SelectContent>
      </Select>
      {mode === 'libre' && (
        <input
          type="number"
          step="0.1"
          className="form-input"
          value={numericValue}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          style={{ width: '90px' }}
          data-testid={testId ? `${testId}-free-input` : undefined}
        />
      )}
    </div>
  );
};

export const ProjectFormModal = ({ show, onClose, onSubmit, form, setForm, editProject }) => {
  if (!show) return null;
  const guarantee = form.guarantee_type || '';
  const contract = form.contract_type || 'direct';
  const isSousTraitance = contract === 'sous_traitance';
  // Marche reference for prorata bidirectional auto-compute
  const marcheRef = Number(form.sale_price_ht || form.marche_total_ht || 0);
  const onProrataPctChange = (v) => {
    const pct = parseFloat(v) || 0;
    const next = { ...form, prorata_percent: pct };
    if (marcheRef > 0) next.prorata_amount = Math.round(marcheRef * pct) / 100;
    setForm(next);
  };
  const onProrataAmountChange = (v) => {
    const amt = parseFloat(v) || 0;
    const next = { ...form, prorata_amount: amt };
    if (marcheRef > 0) next.prorata_percent = Math.round((amt / marcheRef) * 10000) / 100;
    setForm(next);
  };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflow: 'auto', padding: '20px' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '100%', maxWidth: '560px', maxHeight: '95vh', overflow: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 2 }}>
          <h3 style={{ margin: 0 }}>{editProject ? 'Modifier le chantier' : 'Nouveau chantier'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
        </div>
        <form onSubmit={onSubmit} style={{ padding: '24px' }} data-testid="project-form">
          <div className="form-group">
            <label className="form-label">Nom du chantier *</label>
            <input type="text" className="form-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="project-name-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Client</label>
            <ClientPicker
              valueId={form.client_id || ''}
              valueName={form.client_name || ''}
              onChange={(c) => setForm({ ...form, client_id: c.id, client_name: c.name })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Adresse du chantier</label>
            <AddressAutocomplete
              value={form.address}
              onChange={(val, geo) => {
                const updates = { ...form, address: val };
                if (geo) { updates.latitude = geo.lat; updates.longitude = geo.lon; }
                setForm(updates);
              }}
              placeholder="Rechercher une adresse..."
              data-testid="project-address-input"
            />
          </div>

          {/* --- TYPE DE CONTRAT --- */}
          <div className="form-group" style={{ marginTop: 6 }}>
            <label className="form-label" style={{ fontWeight: 700 }}>Type de contrat *</label>
            <Select value={contract} onValueChange={(v) => setForm({ ...form, contract_type: v, autoliquidation_tva: v === 'sous_traitance' ? !!form.autoliquidation_tva : false, tva_rate: v === 'sous_traitance' ? form.tva_rate : (form.tva_rate || 20) })}>
              <SelectTrigger data-testid="project-contract-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="direct">Chantier en direct</SelectItem>
                <SelectItem value="bon_commande">Bon de commande</SelectItem>
                <SelectItem value="sous_traitance">Sous-traitance</SelectItem>
                <SelectItem value="co_traitance">Co-traitance</SelectItem>
                <SelectItem value="appel_offres">Appel d'offres</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {contract === 'bon_commande' && (
            <div className="form-group" style={{ padding: 10, background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8 }}>
              <label className="form-label">N° du bon de commande (a reporter sur chaque facture)</label>
              <input type="text" className="form-input" value={form.purchase_order_number || ''} onChange={(e) => setForm({ ...form, purchase_order_number: e.target.value })} placeholder="BC-2026-001" data-testid="project-po-number" />
              <div style={{ fontSize: '0.75rem', color: '#92400E', marginTop: 6 }}>Optionnel a la creation. Vous pourrez ensuite l'ajouter et joindre le PDF du bon de commande dans l'onglet Gestion.</div>
            </div>
          )}
          {contract === 'sous_traitance' && (
            <div className="form-group" style={{ padding: 10, background: '#EEF2FF', border: '1px solid #4F46E5', borderRadius: 8 }}>
              <label className="form-label">Entreprise mandataire *</label>
              <input type="text" className="form-input" value={form.mandataire_company || ''} onChange={(e) => setForm({ ...form, mandataire_company: e.target.value })} placeholder="Nom de l'entreprise mandataire" data-testid="project-mandataire" required={contract === 'sous_traitance'} />
            </div>
          )}
          {contract === 'co_traitance' && (
            <div className="form-group" style={{ padding: 10, background: '#F0F9FF', border: '1px solid #0EA5E9', borderRadius: 8 }}>
              <label className="form-label">Co-traitant(s) - groupement</label>
              <input type="text" className="form-input" value={form.mandataire_company || ''} onChange={(e) => setForm({ ...form, mandataire_company: e.target.value })} placeholder="Nom du/des co-traitant(s)" data-testid="project-cotraitance" />
            </div>
          )}

          {/* --- TVA AUTOLIQUIDATION (sous-traitance uniquement) --- */}
          {isSousTraitance && (
            <div className="form-group" style={{ padding: '12px 14px', backgroundColor: form.autoliquidation_tva ? '#FFEBEE' : 'var(--bg-soft, #F4F5F7)', border: form.autoliquidation_tva ? '1px solid #C62828' : '1px solid var(--border, #E2E2E6)', borderRadius: '8px', marginTop: '12px', marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }} data-testid="project-autoliq-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!form.autoliquidation_tva}
                  onChange={(e) => setForm({ ...form, autoliquidation_tva: e.target.checked, tva_rate: e.target.checked ? 0 : (form.tva_rate || 20) })}
                  data-testid="project-autoliq-checkbox"
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontWeight: 700, color: form.autoliquidation_tva ? '#C62828' : '#1E2030' }}>TVA 0% AUTOLIQUIDATION</div>
                  <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginTop: '2px' }}>Sous-traitance BTP : la TVA est due par le donneur d'ordre. Force a 0% sur tous devis/factures de ce chantier, avec mention legale automatique.</div>
                </div>
              </label>
            </div>
          )}

          {/* --- GARANTIE FINANCIÈRE --- */}
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label" style={{ fontWeight: 700 }}>Garantie financiere</label>
            <Select value={guarantee || 'none'} onValueChange={(v) => setForm({ ...form, guarantee_type: v === 'none' ? null : v, retention_percent: v === 'retenue' ? (form.retention_percent || 5) : null })}>
              <SelectTrigger data-testid="project-guarantee-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                <SelectItem value="retenue">Retenue de garantie</SelectItem>
                <SelectItem value="caution">Caution bancaire</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {guarantee === 'retenue' && (
            <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: 10, background: '#FFF7ED', border: '1px solid #F97316', borderRadius: 8 }}>
              <div>
                <label className="form-label">Pourcentage de retenue *</label>
                <input type="number" step="0.1" min="0" max="100" className="form-input" value={form.retention_percent ?? 5} onChange={(e) => setForm({ ...form, retention_percent: parseFloat(e.target.value) || 0 })} data-testid="project-retention-pct" />
              </div>
              <div>
                <label className="form-label">Date de liberation prevue</label>
                <input type="date" className="form-input" value={form.retention_release_date || ''} onChange={(e) => setForm({ ...form, retention_release_date: e.target.value })} data-testid="project-retention-release" />
              </div>
              <div style={{ gridColumn: '1 / -1', fontSize: '0.75rem', color: '#9A3412' }}>
                Cette retenue ({form.retention_percent || 5}%) sera deduite du Total HT de chaque facture du chantier ET calculee sur le montant total des devis + avenants. Un rappel sera cree 15 jours avant la date de liberation.
              </div>
            </div>
          )}
          {guarantee === 'caution' && (
            <div className="form-group" style={{ padding: 10, background: '#FEF2F2', border: '1px solid #DC2626', borderRadius: 8 }}>
              <div style={{ fontSize: '0.85rem', color: '#991B1B', fontWeight: 700 }}>Caution bancaire selectionnee</div>
              <div style={{ fontSize: '0.78rem', color: '#7F1D1D', marginTop: 4 }}>Pensez a ajouter l'attestation de caution dans les documents du chantier. Tant qu'elle n'est pas ajoutee, une mention rouge apparaitra a cote du titre du chantier.</div>
            </div>
          )}

          {/* --- PRORATA --- */}
          <div className="form-group" style={{ marginTop: 12, padding: '10px 12px', background: form.prorata_enabled ? '#ECFDF5' : 'var(--bg-soft, #F4F5F7)', border: form.prorata_enabled ? '1px solid #10B981' : '1px solid var(--border, #E2E2E6)', borderRadius: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.prorata_enabled} onChange={(e) => setForm({ ...form, prorata_enabled: e.target.checked })} data-testid="project-prorata-enabled" style={{ width: 18, height: 18 }} />
              <span style={{ fontWeight: 700 }}>Compte prorata</span>
            </label>
            {form.prorata_enabled && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
                <div>
                  <label className="form-label">% du marche</label>
                  <input type="number" step="0.01" min="0" max="100" className="form-input" value={form.prorata_percent || ''} onChange={(e) => onProrataPctChange(e.target.value)} data-testid="project-prorata-pct" />
                </div>
                <div>
                  <label className="form-label">Montant (EUR)</label>
                  <input type="number" step="0.01" className="form-input" value={form.prorata_amount || ''} onChange={(e) => onProrataAmountChange(e.target.value)} data-testid="project-prorata-amount" />
                </div>
                <div style={{ gridColumn: '1 / -1', fontSize: '0.72rem', color: '#065F46' }}>
                  {marcheRef > 0
                    ? <>Reference marche : <strong>{marcheRef.toLocaleString('fr-FR')} EUR HT</strong>. Le calcul %/EUR est automatique.</>
                    : <>Renseignez le montant ou le %. Le calcul auto sera actif des qu'un devis valide existe pour ce chantier.</>}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: 12 }}>
            <div className="form-group">
              <label className="form-label">Date de debut <span style={{ color: '#C62828' }}>*</span></label>
              <input type="date" className="form-input" value={form.start_date || ''} onChange={(e) => setForm({...form, start_date: e.target.value})} required data-testid="project-start-date" />
            </div>
            <div className="form-group">
              <label className="form-label">Date de fin <span style={{ color: '#C62828' }}>*</span></label>
              <input type="date" className="form-input" value={form.end_date || ''} onChange={(e) => setForm({...form, end_date: e.target.value})} required data-testid="project-end-date" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', position: 'sticky', bottom: 0, background: 'white', paddingTop: 12, borderTop: '1px solid #E2E2E6' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Annuler</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="project-submit-btn">{editProject ? 'Enregistrer' : 'Creer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ExpenseFormModal = ({ show, onClose, onSubmit, form, setForm, categories }) => {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '100%', maxWidth: '480px' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Ajouter une depense</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
        </div>
        <form onSubmit={onSubmit} style={{ padding: '24px' }}>
          <div className="form-group">
            <label className="form-label">Categorie *</label>
            <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((cat) => <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <input type="text" className="form-input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Montant HT *</label>
              <input type="number" step="0.01" className="form-input" value={form.amount_ht} onChange={(e) => setForm({...form, amount_ht: parseFloat(e.target.value) || 0})} required />
            </div>
            <div className="form-group">
              <label className="form-label">Taux TVA %</label>
              <TvaSelector value={form.tva_rate} onChange={(v) => setForm({...form, tva_rate: v})} testId="expense-tva-selector" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" className="form-input" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">Heures (optionnel)</label>
              <input type="number" step="0.5" className="form-input" value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})} placeholder="0" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Annuler</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Ajouter</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const PhotoUploadModal = ({ show, onClose, onSubmit, photoFile, setPhotoFile, photoComment, setPhotoComment, photoFolder = '', setPhotoFolder = () => {} }) => {
  if (!show) return null;
  // photoFile may be a single File or an Array of File (multi-upload)
  const files = Array.isArray(photoFile) ? photoFile : (photoFile ? [photoFile] : []);
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '100%', maxWidth: '560px', maxHeight: '95vh', overflow: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 2 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><Camera size={20} /> Ajouter des photos</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
        </div>
        <form onSubmit={onSubmit} style={{ padding: '24px' }}>
          <div className="form-group">
            <label className="form-label">Photos * (selection multiple supportee)</label>
            <DropZone
              onFiles={(newFiles) => {
                const arr = Array.from(newFiles || []);
                setPhotoFile(prev => {
                  const existing = Array.isArray(prev) ? prev : (prev ? [prev] : []);
                  return [...existing, ...arr];
                });
              }}
              accept="image/*"
              multiple={true}
              allowFolders={false}
              testId="photo-upload-dropzone"
              label="Glissez plusieurs photos ici ou cliquez pour en selectionner"
              hint="JPG, PNG, HEIC - selection multiple OK"
              compact
            />
            {files.length > 0 && (
              <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                {files.map((f, idx) => (
                  <div key={`${f.name}-${idx}`} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E2E2E6' }}>
                    <img src={URL.createObjectURL(f)} alt={`Apercu ${idx + 1}`} style={{ width: '100%', height: '100px', objectFit: 'cover', display: 'block' }} />
                    <button type="button" onClick={() => setPhotoFile(prev => {
                      const arr = Array.isArray(prev) ? prev : (prev ? [prev] : []);
                      return arr.filter((_, i) => i !== idx);
                    })} data-testid={`photo-remove-${idx}`} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.65)', border: 'none', color: 'white', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', fontSize: 12 }}>x</button>
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: '0.65rem', padding: '2px 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', color: '#5E5F66', fontWeight: 700 }}>{files.length} photo(s)</div>
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Commentaire (applique a toutes)</label>
            <textarea className="form-input" value={photoComment} onChange={(e) => setPhotoComment(e.target.value)} rows={3} placeholder="Decrivez les photos..." data-testid="photo-comment-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Dossier (optionnel)</label>
            <input
              type="text"
              className="form-input"
              value={photoFolder}
              onChange={(e) => setPhotoFolder(e.target.value)}
              placeholder="Ex : Reception materiel, Avant travaux, Finitions..."
              data-testid="photo-folder-input"
            />
            <small style={{ color: '#5E5F66', fontSize: '0.72rem' }}>Laisser vide pour les photos generales. Sera utilise pour regrouper les photos.</small>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Annuler</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="photo-submit-btn" disabled={files.length === 0}><Camera size={16} /> Envoyer {files.length > 0 ? `(${files.length})` : ''}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const PhotoPreviewModal = ({ photo, onClose, apiUrl }) => {
  if (!photo) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={onClose}>
      <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: 'absolute', top: '-40px', right: '0', color: 'white', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}><X size={32} color="white" /></button>
        <img src={`${apiUrl}${photo.url}`} alt={photo.original_filename} style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: '4px' }} />
        {photo.comment && (
          <div style={{ backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', padding: '12px 16px', borderRadius: '0 0 4px 4px', marginTop: '-4px' }}>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>{photo.comment}</p>
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', opacity: 0.7 }}>Par {photo.uploaded_by_name} - {photo.created_at?.slice(0, 10)}</p>
          </div>
        )}
      </div>
    </div>
  );
};
